import { useEffect, useRef, useState } from 'react';
import { AlertBar } from '@/components/AlertBar';
import { AnimatePresence } from 'motion/react';
import { ArrowDown2, ArrowUp2 } from 'iconsax-reactjs';
import type { Timeframe } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { useKnotSummary, visibleRows, type TakenKnot } from './summary';
import { useI18n } from '@/lib/i18n';
import { useAlertSound } from '@/lib/useAlertSound';
import { formatBrokerTime } from '@/lib/time';
import { timeframeShort } from '@pipyar/core';

/** How long the card stays before it clears itself. */
const LINGER_MS = 60_000;

interface Fired {
  id: string;
  timeframe: Timeframe;
  knot: TakenKnot;
  digits: number;
}

/**
 * Announces a knot being taken, wherever you are in the app.
 *
 * The counterpart to the time-point alert: that one counts DOWN to something
 * scheduled, this one reports something that has just happened. Both live in the
 * app shell because either is worth knowing whichever screen you are on.
 */
export function KnotAlert() {
  const { symbol, instrument } = useSymbol();
  const query = useKnotSummary(symbol);
  const { t } = useI18n();
  const { play } = useAlertSound('knot');
  // Held in a ref so changing the sound in settings does not re-run the effect
  // below, whose dependency is deliberately only the data.
  const playRef = useRef(play);
  playRef.current = play;

  /*
   * The newest take already accounted for, per timeframe.
   *
   * Null until the first answer lands, and that first answer only seeds it —
   * otherwise every knot in history would fire an alert on page load.
   */
  const seen = useRef<Map<string, number> | null>(null);
  const [fired, setFired] = useState<Fired | null>(null);

  const data = query.data;
  useEffect(() => {
    if (!data) return;
    const rows = visibleRows(data);
    if (rows.length === 0) return;

    if (seen.current === null) {
      seen.current = new Map(rows.map((r) => [r.timeframe, r.knot.takenAt]));
      return;
    }

    // If several moved at once, announce the most recent; the rest are on the
    // dashboard tile and stacking cards would bury each other.
    let newest: Fired | null = null;
    for (const r of rows) {
      const previous = seen.current.get(r.timeframe);
      if (previous === undefined || r.knot.takenAt > previous) {
        if (newest === null || r.knot.takenAt > newest.knot.takenAt) {
          newest = {
            id: `${r.timeframe}:${r.knot.takenAt}`,
            timeframe: r.timeframe,
            knot: r.knot,
            digits: data.digits,
          };
        }
      }
      seen.current.set(r.timeframe, r.knot.takenAt);
    }
    if (newest) {
      setFired(newest);
      // Only on a genuine new take — the seeding branch above returns before
      // reaching here, so loading the page does not announce the backlog.
      playRef.current();
    }
  }, [data]);

  // Clears itself: unlike the countdown there is nothing to watch tick down, so
  // leaving it up would just accumulate stale news on the screen.
  useEffect(() => {
    if (!fired) return;
    const id = setTimeout(() => setFired(null), LINGER_MS);
    return () => clearTimeout(id);
  }, [fired]);

  // What the close implies, not which side price arrived from.
  const bullish = fired?.knot.bias === 'bullish';

  return (
    <AnimatePresence>
      {fired && (
        <AlertBar
          key={fired.id}
          accent={bullish ? 'var(--bull)' : 'var(--bear)'}
          icon={bullish ? <ArrowUp2 size={16} variant="Bold" /> : <ArrowDown2 size={16} variant="Bold" />}
          kind={t('knots.alertKind')}
          title={t('knots.alertTaken', { timeframe: timeframeShort(fired.timeframe) })}
          subtitle={
            <>
              {t(bullish ? 'knots.biasBullish' : 'knots.biasBearish')}
              {' · '}
              {/*
                * takenAt is a true UTC instant — the route already removed the
                * broker shift — and formatBrokerTime puts it back, so a daily
                * bar does not read as the previous day.
                */}
              <span className="tabular" title={t('common.brokerTime')}>
                {formatBrokerTime(
                  fired.knot.takenAt,
                  instrument?.storedGmtOffsetSec ?? 0,
                  t('common.today'),
                )}
              </span>
            </>
          }
          valueCaption={t('knots.held', {
            count: fired.knot.barsLater,
            unit: t(fired.knot.barsLater === 1 ? 'common.bar' : 'common.bars'),
          })}
          value={fired.knot.level.toFixed(fired.digits)}
          onClose={() => setFired(null)}
          closeLabel={t('knots.dismissAlert')}
        />
      )}
    </AnimatePresence>
  );
}
